/**
 * Copy de precio DualCTA: precio full + descuento primer mes (admin → landing).
 *
 * ## DataFast / Stripe MRR
 * Estos montos son **suscripción mensual (MRR)**, no one-time:
 * - `fullPriceUsd` = list price /mo → Stripe Price recurrente (Exclusive Member price_1TtZs…, todas variants)
 * - `firstMonthPriceUsd` / DELFU80 = 80% 1er mes **encima** del Price (no cambia el MRR base)
 * - Checkout real: DualCTA → `startVipMembershipVideoCheckout` → `/api/create-subscription-checkout`
 *   (mode=subscription + discounts[0][promotion_code] + metadata datafast_*).
 * - MuxVideoPlayerWithCTA: si el parent pasa `onCTAClick` (DualCTA), el VIP es MRR vía parent;
 *   `create-payment-intent` en Mux es packs one-time (no Dual CTA VIP).
 *
 * @see https://datafa.st/docs/stripe-checkout-api
 */

import {
  CANONICAL_VIP_PRICE_USD,
  SELECT_VIP_FIRST_MONTH_DISCOUNT_PERCENT,
} from "@/lib/canonicalVipMembership";

export function formatUsdAmount(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatCurrencyAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function firstMonthPriceUsd(fullUsd: number, discountPercent: number): number {
  const pct = Math.min(100, Math.max(0, discountPercent));
  return Math.round(fullUsd * (1 - pct / 100) * 100) / 100;
}

/**
 * Exterior DualCTA list price for UI — always Stripe Exclusive Member list ($39.99).
 * Do not use admin dual_cta.fullPriceUsd (often 29.99) or DELFU80 shows $6 not $8.
 */
export const EXTERIOR_VIP_LIST_PRICE_USD = CANONICAL_VIP_PRICE_USD;

/** 80% off list → first month (DELFU80 / DELFU80INTL). */
export function exteriorDelfu80FirstMonthUsd(
  listUsd: number = EXTERIOR_VIP_LIST_PRICE_USD,
): number {
  return firstMonthPriceUsd(listUsd, SELECT_VIP_FIRST_MONTH_DISCOUNT_PERCENT);
}

/** Ej: "~~$9.99~~ $4.99 first month" */
export function formatDualCtaFirstMonthOffer(
  fullPriceUsd: number,
  discountPercent: number,
): {
  full: string;
  firstMonth: string;
  line: string;
  shortLine: string;
  /** Pill: "80% OFF" */
  badge: string;
  /** CTA subline ES/EN ready numbers only: "$8 primer mes" built by UI */
  firstMonthUsd: number;
  discountPercent: number;
} {
  const full = formatCurrencyAmount(fullPriceUsd, "USD");
  const firstUsd = firstMonthPriceUsd(fullPriceUsd, discountPercent);
  const first = formatCurrencyAmount(firstUsd, "USD");
  const pct = Math.round(discountPercent);
  return {
    full,
    firstMonth: first,
    line: `${full}/mo → ${first} first month (${pct}% off)`,
    shortLine: `${first} first month · then ${full}/mo`,
    badge: `${pct}% OFF`,
    firstMonthUsd: firstUsd,
    discountPercent: pct,
  };
}

/** Stripe Checkout submit banner when exterior engagement promo is applied. */
export function exteriorVipStripeCheckoutSubmitMessage(opts: {
  language: "es" | "en";
  listUsd?: number;
  discountPercent?: number;
  supportEmail?: string;
}): string {
  const list = opts.listUsd ?? EXTERIOR_VIP_LIST_PRICE_USD;
  const pct = opts.discountPercent ?? SELECT_VIP_FIRST_MONTH_DISCOUNT_PERCENT;
  const first = formatCurrencyAmount(firstMonthPriceUsd(list, pct), "USD");
  const full = formatCurrencyAmount(list, "USD");
  if (opts.language === "en") {
    const base = `First month ${pct}% OFF (${first}), then ${full}/mo. Cancel anytime.`;
    return opts.supportEmail
      ? `${base} Questions? ${opts.supportEmail}`
      : base;
  }
  const base = `Primer mes ${pct}% OFF (${first}), luego ${full}/mes. Cancelás cuando quieras.`;
  return opts.supportEmail
    ? `${base} ¿Dudas? ${opts.supportEmail}`
    : base;
}

export function formatAmountFromCents(
  amountCents: number,
  currency: string,
): string {
  return formatCurrencyAmount(amountCents / 100, currency);
}

/**
 * Slack / telemetry label for the first-month offer.
 * Prefer promo_ id when checkout uses a real Promotion Code; fall back to PROMO{N}_1M.
 */
export function formatCouponHint(
  discountPercent: number,
  stripePromotionCodeId?: string | null,
): string {
  const promo = stripePromotionCodeId?.trim();
  if (promo?.startsWith("promo_")) {
    const n = Math.round(discountPercent);
    const pct = n > 0 ? `${n}% 1er mes · ` : "";
    return `${pct}${promo} (Stripe promo)`;
  }
  const n = Math.round(discountPercent);
  return `PROMO${n}_1M (Stripe, 1 mes)`;
}